'use client';
import { useTranslation, useWindowDimensions } from '@/hook';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
    CarProfileSectionHeaderContainer,
    CarProfileSectionHeaderTitle,
    CarProfileSectionViewMore,
    CarProfileSectionViewMoreNoBorder,
    CarProfileViewMoreContainer,
    CarProfileViewMoreContainerMobile,
} from '../StyledComponents/section-header-components';
import {
    ImperfectionContainer,
    ImperfectionHeadeContainer,
    ImperfectionHeaderHighlightedText,
    ImperfectionHeaderIcon,
    ImperfectionHeaderText,
    ImperfectionItemContainer,
    ImperfectionStatusesContainer,
    SectionFooterContainer,
} from '../StyledComponents/imperfection-section-components';
import { InspectionPoints } from '../../../../../public/Images';
import ImperfectionStatusLegend from './imperfection-status-legend.component';
import {
    ImperfectionReportItem,
    ImperfectionStatusEnum,
} from '@/types/imperfection-report.types';

import Conditional from '@/components/shared/conditional/conditional.component';
import { IImperfectionReportItemTypes } from '@/types';
import ImperfectionItem from '@/components/Imperfections/imperfection-item.component';

export interface IImperfectionOverview {
    section_ar: string;
    section_en: string;
    faultyPoints: number;
    fixedPoints: number;
    sectionGoodIcon: string;
    sectionFaultyIcon: string;
    item_type: IImperfectionReportItemTypes;
    items: ImperfectionReportItem[];
}

const ImperfectionOverview = ({
    imperfectionItems,
    sectionHeader,
    viewAll,
    itemClicked,
}: {
    imperfectionItems: IImperfectionOverview[];
    sectionHeader: string;
    viewAll?: string;
    itemClicked: (id: number, isViewAll: boolean) => void;
}) => {
    const { t, IS_RTL } = useTranslation();
    const { IS_DESKTOP } = useWindowDimensions();

    const lastItem = imperfectionItems.length - 1;

    const generateImperfectionSection = (
        section: IImperfectionOverview,
        index: number
    ) => {
        const elements = [];
        if (section.item_type === IImperfectionReportItemTypes.TEXT_ONLY) {
            for (const item of section.items) {
                if (
                    !item.comment_ar ||
                    !item.comment_en ||
                    item.comment_ar === '' ||
                    item.comment_en === ''
                ) {
                    continue;
                }

                elements.push(
                    <ImperfectionItemContainer
                        style={{ border: index === lastItem ? 0 : '' }}
                        key={section?.section_en}
                    >
                        <ImperfectionItem
                            key={section?.section_en + ' Item'}
                            title_ar={item?.comment_ar}
                            title_en={item?.comment_en}
                            faultyItemsNumber={section?.faultyPoints}
                            fixedItemsNumber={section?.fixedPoints}
                            icon={{
                                good: section?.sectionGoodIcon,
                                faulty: section?.sectionFaultyIcon,
                            }}
                            section_type={section?.item_type}
                            textOnlyValueAr={item.comment_ar}
                            textOnlyValueEn={item.comment_en}
                            ImperfectionStatus={item.status}
                        />
                    </ImperfectionItemContainer>
                );
            }
        } else {
            return (
                <ImperfectionItemContainer
                    style={{ border: index === lastItem ? 0 : '' }}
                    key={section?.section_en}
                    onClick={() => itemClicked(index, false)}
                >
                    <ImperfectionItem
                        key={section?.section_en + ' Item'}
                        title_ar={section?.section_ar}
                        title_en={section?.section_en}
                        faultyItemsNumber={section?.faultyPoints}
                        fixedItemsNumber={section?.fixedPoints}
                        icon={{
                            good: section?.sectionGoodIcon,
                            faulty: section?.sectionFaultyIcon,
                        }}
                        section_type={section?.item_type}
                        textOnlyValueAr={''}
                        textOnlyValueEn={''}
                        ImperfectionStatus={section.items[0].status}
                    />
                </ImperfectionItemContainer>
            );
        }

        return elements;
    };

    return (
        <>
            <CarProfileSectionHeaderContainer>
                <CarProfileSectionHeaderTitle variant={'subtitle1Bold'}>
                    {sectionHeader}
                </CarProfileSectionHeaderTitle>
                <Conditional condition={viewAll !== undefined && !IS_DESKTOP}>
                    <CarProfileSectionViewMoreNoBorder
                        onClick={() => itemClicked(0, true)}
                    >
                        <CarProfileSectionViewMore>
                            {viewAll}
                        </CarProfileSectionViewMore>
                        {IS_RTL ? (
                            <ChevronLeftIcon sx={{ color: '#00673E' }} />
                        ) : (
                            <ChevronRightIcon sx={{ color: '#667085' }} />
                        )}
                    </CarProfileSectionViewMoreNoBorder>
                </Conditional>
            </CarProfileSectionHeaderContainer>

            <ImperfectionContainer>
                <ImperfectionHeadeContainer>
                    <ImperfectionHeaderIcon src={InspectionPoints} alt="" />
                    <ImperfectionHeaderText variant="body1">
                        {`${t('inspectionReportParagraph1')} `}
                        <ImperfectionHeaderHighlightedText variant="body1Bold">
                            {t('inspectionReportParagraph2')}
                        </ImperfectionHeaderHighlightedText>
                        {t('inspectionReportParagraph3')}
                    </ImperfectionHeaderText>
                </ImperfectionHeadeContainer>
                {imperfectionItems.map((section, index) =>
                    generateImperfectionSection(section, index)
                )}
                <SectionFooterContainer isDesktop={IS_DESKTOP === true}>
                    <Conditional
                        condition={viewAll !== undefined && IS_DESKTOP === true}
                    >
                        <CarProfileViewMoreContainer
                            onClick={() => itemClicked(0, true)}
                        >
                            <CarProfileSectionViewMore>
                                {viewAll}
                            </CarProfileSectionViewMore>
                            {IS_RTL ? (
                                <ChevronLeftIcon sx={{ color: '#00673E' }} />
                            ) : (
                                <ChevronRightIcon sx={{ color: '#667085' }} />
                            )}
                        </CarProfileViewMoreContainer>
                    </Conditional>
                    <ImperfectionStatusesContainer>
                        <ImperfectionStatusLegend
                            status={ImperfectionStatusEnum.UnFaulty}
                        />
                        <ImperfectionStatusLegend
                            status={ImperfectionStatusEnum.Fixed}
                        />
                        <ImperfectionStatusLegend
                            status={ImperfectionStatusEnum.Faulty}
                        />
                    </ImperfectionStatusesContainer>
                </SectionFooterContainer>
            </ImperfectionContainer>
            <Conditional condition={viewAll !== undefined && !IS_DESKTOP}>
                <CarProfileViewMoreContainerMobile
                    onClick={() => itemClicked(0, true)}
                >
                    <CarProfileSectionViewMore>
                        {viewAll}
                    </CarProfileSectionViewMore>
                    {IS_RTL ? (
                        <ChevronLeftIcon sx={{ color: '#00673E' }} />
                    ) : (
                        <ChevronRightIcon sx={{ color: '#667085' }} />
                    )}
                </CarProfileViewMoreContainerMobile>
            </Conditional>
        </>
    );
};
export default ImperfectionOverview;
